import React from 'react';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const RuleFilters = ({ 
  filters, 
  onFilterChange, 
  onClearFilters, 
  resultsCount = 0,
  onAddRule,
  onBulkEdit,
  onImportExport 
}) => {
  const jurisdictionOptions = [
    { value: 'all', label: 'All Jurisdictions' },
    { value: 'federal', label: 'Federal' },
    { value: 'state', label: 'State Level' },
    { value: 'cfpb', label: 'CFPB' },
    { value: 'fdic', label: 'FDIC' },
    { value: 'occ', label: 'OCC' },
    { value: 'fed', label: 'Federal Reserve' },
    { value: 'sec', label: 'SEC' },
    { value: 'finra', label: 'FINRA' }
  ];

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'disclosure', label: 'Disclosure Requirements' },
    { value: 'lending', label: 'Lending Practices' },
    { value: 'privacy', label: 'Privacy & Data Protection' },
    { value: 'advertising', label: 'Advertising Standards' },
    { value: 'aml', label: 'Anti-Money Laundering' },
    { value: 'kyc', label: 'Know Your Customer' },
    { value: 'fair-lending', label: 'Fair Lending' },
    { value: 'consumer-protection', label: 'Consumer Protection' }
  ];

  const severityOptions = [
    { value: 'all', label: 'All Severity Levels' },
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending Review' },
    { value: 'deprecated', label: 'Deprecated' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      {/* Header with Actions */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-1">Regulatory Rules Library</h2>
          <p className="text-sm text-muted-foreground">
            Manage and configure compliance rules for automated content analysis
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4 lg:mt-0">
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
            onClick={onImportExport}
          >
            Import/Export
          </Button>
          <Button
            variant="outline"
            iconName="Edit"
            iconPosition="left"
            onClick={onBulkEdit}
          >
            Bulk Edit
          </Button>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={onAddRule}
          >
            Add Custom Rule
          </Button>
        </div>
      </div>
      {/* Search and Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-4">
        <div className="lg:col-span-2">
          <Input
            type="search"
            placeholder="Search rules by name, description, or regulatory text..."
            value={filters?.search || ''}
            onChange={(e) => onFilterChange('search', e?.target?.value)}
            className="w-full"
          />
        </div>
        
        <Select
          placeholder="Select jurisdiction"
          options={jurisdictionOptions}
          value={filters?.jurisdiction || 'all'}
          onChange={(value) => onFilterChange('jurisdiction', value)}
        />
        
        <Select
          placeholder="Select category"
          options={categoryOptions}
          value={filters?.category || 'all'}
          onChange={(value) => onFilterChange('category', value)}
        />
        
        <Select
          placeholder="Select severity"
          options={severityOptions}
          value={filters?.severity || 'all'}
          onChange={(value) => onFilterChange('severity', value)}
        />
      </div>
      {/* Additional Filters Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Select
          placeholder="Select status"
          options={statusOptions}
          value={filters?.status || 'all'}
          onChange={(value) => onFilterChange('status', value)}
        />
        
        <Input
          type="date"
          label="Updated after"
          value={filters?.updatedAfter || ''}
          onChange={(e) => onFilterChange('updatedAfter', e?.target?.value)}
        />
        
        <Input
          type="date"
          label="Updated before"
          value={filters?.updatedBefore || ''}
          onChange={(e) => onFilterChange('updatedBefore', e?.target?.value)}
        />
        
        <div className="flex items-end">
          <Button
            variant="outline"
            iconName="X"
            iconPosition="left"
            onClick={onClearFilters}
            className="w-full"
          >
            Clear Filters
          </Button>
        </div>
      </div>
      {/* Results Summary */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-4">
          <span className="text-sm text-muted-foreground">
            Showing <span className="font-medium text-foreground">{resultsCount}</span> rules
          </span>
          {(filters?.search || filters?.jurisdiction !== 'all' || filters?.category !== 'all' || 
            filters?.severity !== 'all' || filters?.status !== 'all') && (
            <div className="flex items-center space-x-2">
              <Icon name="Filter" size={14} className="text-primary" />
              <span className="text-sm text-primary">Filters applied</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Clock" size={14} />
          <span>Last updated: Sep 20, 2025</span>
        </div>
      </div>
    </div>
  );
};

export default RuleFilters;